import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useReducer,
  useState,
} from 'react';
import { Layout } from 'react-grid-layout';
import { useNavigate, useParams } from 'react-router-dom';
import {
  DashboardService,
  type DashboardWidgetPersisted,
} from '../../../service/DashboardService';
import {
  ActionTypes,
  dashboardReducer,
  DashboardState,
  IActionType,
} from '../Reducer';

export interface Widget {
  id?: string;
  type: 'chatbot' | 'form' | 'flow' | 'header' | 'tutorials' | 'flows';
  title: string;
  chatbotData?: any;
  formData?: any;
  flowData?: any;
  layout: Layout;
  spaceName?: string;
}

type WidgetCreateDraft = Omit<Widget, 'id'> & { layout: Layout };

function layoutDimensionFromApi(value: unknown): number | undefined {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const n = Number(value);
    if (!Number.isNaN(n)) {
      return n;
    }
  }
  return undefined;
}

function mergeWidgetLayoutFromApi(
  base: Layout,
  apiLayout: unknown,
  serverWidgetId: string,
): Layout {
  const merged: Layout = { ...base, i: serverWidgetId };
  if (!apiLayout || typeof apiLayout !== 'object' || Array.isArray(apiLayout)) {
    return merged;
  }
  const o = apiLayout as Record<string, unknown>;
  const keys = ['x', 'y', 'w', 'h', 'minW', 'minH', 'maxW', 'maxH'] as const;
  for (const k of keys) {
    const v = layoutDimensionFromApi(o[k]);
    if (v !== undefined) {
      Object.assign(merged, { [k]: v });
    }
  }
  return merged;
}

function widgetFromCreateResponse(
  draft: WidgetCreateDraft,
  persisted: DashboardWidgetPersisted,
): Widget {
  const serverId = persisted.id;
  return {
    id: serverId,
    type: persisted.type,
    title: persisted.title,
    layout: mergeWidgetLayoutFromApi(draft.layout, persisted.layout, serverId),
    chatbotData: persisted.chatbotData ?? draft.chatbotData,
    formData: persisted.formData ?? draft.formData,
    flowData: persisted.flowData ?? draft.flowData,
    spaceName: draft.spaceName,
  };
}

interface DashboardContextType {
  state: DashboardState;
  dispatch: React.Dispatch<IActionType>;
  loadingDashboard: boolean;
  modalOpen: boolean;
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  addWidget: ({
    type,
    widgetData,
  }: {
    type: 'chatbot' | 'form' | 'flow' | 'header' | 'tutorials' | 'flows';
    widgetData?: any;
  }) => Promise<void>;
  removeWidget: (widgetId: string) => Promise<void>;
  dashboardId: string | null;
}

const DashboardContext = createContext<DashboardContextType | undefined>(
  undefined,
);

interface DashboardProviderProps {
  children: ReactNode;
  dashboardId?: string;
}

const initialState: DashboardState = {
  dashboard: {
    id: '',
    status: 'draft',
    widgets: [],
    createdAt: '',
    updatedAt: '',
  },
  isInitialized: false,
};

function getErrorStatusCode(error: object): number | undefined {
  if (
    'statusCode' in error &&
    typeof (error as { statusCode: number }).statusCode === 'number'
  ) {
    return (error as { statusCode: number }).statusCode;
  }
  return undefined;
}

export function DashboardProvider({
  children,
  dashboardId: propDashboardId,
}: DashboardProviderProps) {
  const { dashboardId: paramDashboardId } = useParams();
  const dashboardId = propDashboardId || paramDashboardId || null;
  const navigate = useNavigate();

  const [state, dispatch] = useReducer(dashboardReducer, initialState);
  const [loadingDashboard, setLoadingDashboard] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    const getDashboard = async () => {
      if (!dashboardId) {
        setLoadingDashboard(false);
        return;
      }

      setLoadingDashboard(true);
      try {
        const response = await DashboardService.getDashboard(dashboardId);

        dispatch({
          type: ActionTypes.UPDATE_DASHBOARD,
          payload: { dashboard: response.data },
        });
      } catch (error) {
        console.error('Error fetching dashboard:', error);
        if (
          error &&
          typeof error === 'object' &&
          getErrorStatusCode(error) === 403
        ) {
          navigate('/dashboard-beta', { replace: true });
        }
      } finally {
        setLoadingDashboard(false);
      }
    };

    getDashboard();
  }, [dashboardId, navigate]);

  async function addWidget({
    type,
    widgetData,
  }: {
    type: 'chatbot' | 'form' | 'flow' | 'header' | 'tutorials' | 'flows';
    widgetData?: any;
  }) {
    if (!dashboardId) return;

    try {
      const layoutTempId = crypto.randomUUID();
      const stackY = Math.max(
        ...state.dashboard.widgets.map(item => item.layout.y + item.layout.h),
        0,
      );

      let draftWidget: WidgetCreateDraft;

      switch (type) {
        case 'chatbot':
          draftWidget = {
            type: 'chatbot',
            title: widgetData?.name ?? 'Chatbot Widget',
            chatbotData: widgetData,
            layout: {
              i: layoutTempId,
              x: 0,
              y: stackY,
              w: 3,
              h: 3,
              minW: 2,
              minH: 3,
            },
          };
          break;
        case 'form':
          draftWidget = {
            type: 'form',
            title: widgetData?.name ?? 'Interface Widget',
            layout: {
              i: layoutTempId,
              x: 0,
              y: stackY,
              w: 3,
              h: 3,
              minW: 2,
              minH: 3,
            },
          };
          if (widgetData?.id && widgetData?.isForm) {
            draftWidget.flowData = widgetData;
          } else {
            draftWidget.formData = widgetData;
          }
          break;
        case 'flow':
          draftWidget = {
            type: 'flow',
            title: widgetData?.name ?? 'Agent Widget',
            flowData: widgetData,
            layout: {
              i: layoutTempId,
              x: 0,
              y: stackY,
              w: 3,
              h: 3,
              minW: 2,
              minH: 3,
            },
          };
          break;
        case 'header':
          draftWidget = {
            type: 'header',
            title: 'Dashboard Header',
            layout: {
              i: layoutTempId,
              x: 0,
              y: 0,
              w: 12,
              h: 3,
              minW: 12,
              minH: 3,
              maxW: 12,
            },
          };
          break;
        case 'tutorials': {
          const tutorialsY = Math.max(
            ...state.dashboard.widgets
              .filter(w => w.type === 'header')
              .map(item => item.layout.y + item.layout.h),
            0,
          );
          draftWidget = {
            type: 'tutorials',
            title: 'Flux Tutorials',
            layout: {
              i: layoutTempId,
              x: 0,
              y: tutorialsY,
              w: 12,
              h: 5,
              minW: 6,
              minH: 4,
            },
          };
          break;
        }
        case 'flows':
          draftWidget = {
            type: 'flows',
            title: 'Recently Opened & Shared Files',
            layout: {
              i: layoutTempId,
              x: 0,
              y: stackY,
              w: 12,
              h: 6,
              minW: 6,
              minH: 4,
            },
          };
          break;
      }

      const createResponse = await DashboardService.addWidget({
        dashboardId,
        widgetData: draftWidget,
      });

      const persisted = widgetFromCreateResponse(
        draftWidget,
        createResponse.data,
      );

      dispatch({
        type: ActionTypes.ADD_WIDGET,
        payload: persisted,
      });
    } catch (error) {
      console.error('Error adding widget:', error);
    }
  }

  async function removeWidget(widgetId: string) {
    try {
      await DashboardService.removeWidget(widgetId);

      dispatch({
        type: ActionTypes.REMOVE_WIDGET,
        payload: widgetId,
      });
    } catch (error) {
      console.error('Error removing widget:', error);
    }
  }

  return (
    <DashboardContext.Provider
      value={{
        state,
        dispatch,
        loadingDashboard,
        modalOpen,
        setModalOpen,
        addWidget,
        removeWidget,
        dashboardId,
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
}

export function useDashboard() {
  const context = useContext(DashboardContext);

  if (context === undefined) {
    throw new Error('useDashboard must be used within a DashboardProvider');
  }
  return context;
}
